import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CssBaseline from '@mui/material/CssBaseline';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Hidden from '@mui/material/Hidden';
import Drawer from './Drawer';
import etiq_logo from '../../../static/etiq_logo_white.svg';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../../../utils/axiosApi';
import { useDispatch } from 'react-redux';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import { Alert, CircularProgress } from '@mui/material';
import Email from '../../login/email';
import {
  loginUser,
  preLoginUser,
  resendOTP,
} from '../../../utils/axiosRequests';
import { selectEmail } from '../../subscription/form/subscriptionSlice';
import Zoom from '@mui/material/Zoom';
import PhoneIcon from '@mui/icons-material/Phone';
import MailIcon from '@mui/icons-material/Mail';
import { hideOnMobile } from '../../../utils/styles';
import { useGetActiveMaintenancesQuery } from '../../maintenance/maintenanceSlice';
import { OTP } from '@operatech1/ui-components-etiq';
import { datadogRum } from '@datadog/browser-rum';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  appBar: {
    background: '#32325d',
    boxShadow: 'none',
  },
  toolbar: {
    minHeight: '75px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  navLinks: {
    color: '#ffffff',
    textDecoration: 'none',
    marginRight: theme.spacing(4),
    alignItems: 'center',
    '&:hover': {
      color: '#a0a8ff',
    },
  },
  navButtons: {
    borderRadius: '24px',
    padding: '8px 24px',
    textTransform: 'none',
    color: '#ffffff',
    borderColor: '#ffffff',
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    width: '14rem',
    '&:hover': {
      color: '#a0a8ff',
      borderColor: '#a0a8ff',
    },
  },
  children: {
    top: 64,
    width: '100%',
    textAlign: 'center',
    position: 'absolute',
    paddingTop: '40px',
    [theme.breakpoints.down('sm')]: {
      top: 55,
    },
  },
  textLogo: {
    position: 'absolute',
    left: '50%',
    top: 64,
    transition: 'transform 0.2s ease 0s',
    width: theme.spacing(10),
    height: theme.spacing(10),
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(6),
      height: theme.spacing(6),
    },
  },
  textLogoTrigger: {
    transform: ' translateX(-50%) scale(0.5) translateY(-144px)',
    [theme.breakpoints.down('md')]: {
      transform: ' translateX(-50%) scale(1) translateY(-54px)',
    },
  },
  textLogoNotTrigger: {
    left: '50%',
    transform: 'translateX(-50%) scale(1) translateY(-50%)',
  },
}));

function ElevationScroll(props) {
  const { children } = props;

  return React.cloneElement(children, {
    elevation: 1,
  });
}

ElevationScroll.propTypes = {
  children: PropTypes.element.isRequired,
};

export default function ElevateAppBar(props) {
  const { t } = useTranslation('home');
  const [state, setState] = React.useState('start');
  const [isOtp, setIsOtp] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [userId, setUserId] = React.useState(null);
  const [typeUser, setTypeUser] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const [errorMessage, setErrorMessage] = React.useState(null);
  const classes = useStyles();
  const dispatch = useDispatch();
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });
  const navigate = useNavigate();
  useEffect(() => {
    setErrorMessage(null);
    setIsOtp(false);
  }, [open]);

  const onSubmit = (data) => {
    setIsLoading(true);
    setErrorMessage(null);
    if (!isOtp) {
      data = { cat: state, ...data };
      preLoginUser(data)
        .then((response) => {
          setEmail(data.email);
          setPhone(response?.data?.phone);
          setUserId(response?.data?.id);
          setTypeUser(response?.data?.type);
          dispatch(
            selectEmail(
              `${data.email.substring(0, 3)}***@${
                data.email.split('@')[1]
              }`,
            ),
          );
          setIsOtp(true);
          datadogRum.setUser({
            id: response?.data?.id,
            name: data.email,
            email: data.email,
            category: state,
          });
        })
        .catch((error) => {
          setErrorMessage(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      return loginUser({ cat: state, email, ...data })
        .then((response) => {
          setIsLoading(false);
          navigate('/account');
          return Promise.resolve(response);
        })
        .catch((error) => {
          setIsLoading(false);
          setErrorMessage(error);
          if (
            error ===
            "Trop d'essais erronés, veuillez attendre 2 minutes avant de réessayer."
          ) {
            setIsOtp(false);
          }
          return Promise.reject(error);
        });
    }
  };

  const sendOTP = (method, info) => {
    setIsLoading(true);
    resendOTP({
      method,
      id: userId,
      type: typeUser,
      email,
      phone,
      cat: state,
    })
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        setErrorMessage(error.response?.data);
        setIsLoading(false);
      });
  };

  const { data } = useGetActiveMaintenancesQuery();

  const isMaintenanceActive = data?.active;
  const maintenanceMessage = data?.message;

  return (
    <React.Fragment>
      <CssBaseline />
      <ElevationScroll {...props} trigger={trigger}>
        <AppBar className={classes.appBar}>
          {isAuthenticated() && isMaintenanceActive && (
            <Alert severity="error" variant="filled">
              <Typography
                dangerouslySetInnerHTML={{
                  __html: maintenanceMessage,
                }}
              />
            </Alert>
          )}
          <Toolbar
            className={classes.toolbar}
            style={{ height: 'inherit' }}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              flexGrow="1"
            >
              <Drawer
                setModalOpen={setOpen}
                setModalState={setState}
              />
              <Typography
                sx={{
                  fontSize: {
                    sm: '16px',
                    md: '18px',
                    marginRight: '32px',
                  },
                }}
              >
                <img
                  src={etiq_logo}
                  alt="etiq_logo"
                  onClick={() => {
                    navigate('/');
                  }}
                  style={{
                    margin: '',
                    width: '120px',
                    display: 'block',
                    cursor: 'pointer',
                  }}
                />
              </Typography>
              <Link
                variant="h8"
                href={`tel:${process.env.REACT_APP_PHONE_NUMBER}`}
                className={classes.navLinks}
                style={{ maxWidth: '15rem' }}
                sx={{ display: 'flex' }}
              >
                <PhoneIcon
                  style={{
                    verticalAlign: 'middle',
                    marginRight: '5px',
                  }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    ...hideOnMobile(),
                  }}
                >
                  {process.env.REACT_APP_PHONE_NUMBER}{' '}
                  {t('call_free')}
                </Typography>
              </Link>
              <Link
                variant="h8"
                href={`mailto:hello@etiq.com`}
                className={classes.navLinks}
                style={{ maxWidth: '12rem' }}
                sx={{ display: 'flex' }}
              >
                <MailIcon
                  style={{
                    verticalAlign: 'middle',
                    marginRight: '5px',
                  }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{ ...hideOnMobile() }}
                >
                  hello@etiq.com
                </Typography>
              </Link>
            </Stack>
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              flexGrow={0}
              flexBasis={'max-content'}
              sx={{ ...hideOnMobile() }}
            >
              {isAuthenticated() ? (
                <Button
                  color="button"
                  variant="outlined"
                  className={classes.navButtons}
                  onClick={() => navigate('/account')}
                  disabled={isMaintenanceActive}
                >
                  {props.asPro ? t('login_pro') : t('login_classic')}
                </Button>
              ) : (
                <>
                  <Button
                    className={classes.navButtons}
                    variant="outlined"
                    data-cy="login-button"
                    onClick={() => {
                      setState('particulier');
                      setOpen(true);
                    }}
                  >
                    <Hidden smDown>{t('login_classic')}</Hidden>
                    <Hidden smUp>{t('login_classic_short')}</Hidden>
                  </Button>
                  <Button
                    className={classes.navButtons}
                    variant="outlined"
                    onClick={() => {
                      setState('professionnel');
                      setOpen(true);
                    }}
                  >
                    <Hidden smDown>{t('login_pro')}</Hidden>
                    <Hidden smUp>{t('login_pro_short')}</Hidden>
                  </Button>
                </>
              )}
            </Stack>
          </Toolbar>
        </AppBar>
      </ElevationScroll>
      <Toolbar />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        closeAfterTransition
        TransitionComponent={Transition}
        transitionDuration={500}
        maxWidth={'sm'}
        fullWidth
        PaperProps={{
          sx: {
            width: '650px',
            height: '450px',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center',
            padding: '16px',
          },
        }}
      >
        <>
          <Typography variant={'h1'}>{t('login_title')}</Typography>
          {!isOtp ? (
            <Email onSubmit={onSubmit} errorMessage={errorMessage} />
          ) : (
            <OTP
              contactMail={email}
              contactPhone={phone}
              onSubmit={onSubmit}
              onMethodChange={sendOTP}
              onResendCode={sendOTP}
              timeToBlock={new Date(0, 0, 0, 0, 2, 0)}
              containerProps={{
                style: {
                  'text-align': '-webkit-center',
                },
              }}
            />
          )}
          {isLoading && (
            <div
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress />
            </div>
          )}
        </>
      </Dialog>
      <div className={classes.children}>{children}</div>
    </React.Fragment>
  );
}
